import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Num, T, useGT } from 'gt-react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import type { CashReceiptDetail } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  useArPaymentApplicationsList,
  useDeleteArPaymentApplication,
} from '@/api/rpc/hooks/accounts-receivable/useArPaymentApplications';
import { isManualOrigin } from '@/components/common/OriginReadOnlyBadge';
import { useToast } from 'src/context/ToastContext';
import { ApplyToInvoiceDialog } from './ApplyToInvoiceDialog';
import { formatAmount, formatDate } from './cashReceiptFormat';

interface CashReceiptApplicationsSectionProps {
  receipt: CashReceiptDetail;
}

const PAGE_LIMIT = 100;

/**
 * The invoices this cash receipt is applied to. Manual receipts can be applied
 * by hand here; only manually-created applications can be removed (the server
 * refuses the rest — remittance and ERP applications belong to their source).
 * The total comes from the server, so it covers rows past the first page.
 */
export function CashReceiptApplicationsSection({
  receipt,
}: CashReceiptApplicationsSectionProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const [applyOpen, setApplyOpen] = useState(false);
  const { paymentApplications, pagination, data, isLoading, isError, refetch } =
    useArPaymentApplicationsList({
      bankTransactionId: receipt.id,
      sortBy: 'applicationDate',
      sortDirection: 'asc',
      limit: PAGE_LIMIT,
    });
  const deleteApplication = useDeleteArPaymentApplication();
  const editable = isManualOrigin(receipt.originSystem);
  // Until the list loads, the unapplied amount is unknown: no applying.
  const loaded = data != null;

  const totalApplied = Number(data?.totalAppliedAmount ?? 0);
  const totalCount = pagination?.total ?? paymentApplications.length;
  const hiddenCount = Math.max(totalCount - paymentApplications.length, 0);
  const receiptAmount = receipt.paymentAmount
    ? Number(receipt.paymentAmount)
    : 0;
  const unapplied = Math.max(receiptAmount - totalApplied, 0);

  async function handleRemove(applicationId: string) {
    try {
      await deleteApplication.mutateAsync({ id: applicationId });
      showToast({
        title: gt('Application removed', {
          context:
            'toast title, cash receipt application to an invoice removed',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showToast({
        title: gt('Remove failed', {
          context: 'toast title, removing a cash receipt application failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  const removingId = deleteApplication.isPending
    ? deleteApplication.variables?.id
    : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <T context="AR cash receipt, section heading for the invoices the receipt is applied to">
            Applied to invoices
          </T>
        </p>
        {editable && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={!loaded}
            onClick={() => setApplyOpen(true)}
          >
            <PlusIcon className="mr-1 size-3.5" weight="bold" />
            <T context="button label, apply cash receipt to an invoice">
              Apply to invoice
            </T>
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          <T context="loading state for AR cash receipt invoice applications">
            Loading...
          </T>
        </p>
      ) : isError && !loaded ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 p-3">
          <p className="text-sm text-destructive">
            <T context="AR cash receipt, the invoice applications could not be loaded">
              Couldn&apos;t load the invoices this receipt is applied to.
            </T>
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => void refetch()}
          >
            <T context="button label, retry loading cash receipt applications">
              Retry
            </T>
          </Button>
        </div>
      ) : paymentApplications.length === 0 ? (
        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="text-sm text-muted-foreground">
            <T context="AR cash receipt when it is not applied to any invoice">
              Not applied to any invoice yet.
            </T>
          </p>
        </div>
      ) : (
        <div className="rounded-md border">
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 border-b px-3 py-1.5 text-xs font-medium text-muted-foreground">
            <span>
              <T context="column header, invoice number">Invoice #</T>
            </span>
            <span className="text-right">
              <T context="column header, amount of the receipt applied to the invoice">
                Applied
              </T>
            </span>
            <span>
              <T context="column header, date the receipt was applied">Date</T>
            </span>
            <span className="w-7" />
          </div>
          {paymentApplications.map((app) => (
            <div
              key={app.id}
              className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-3 py-1.5 text-sm"
            >
              <Link
                to="/accounts-receivable/invoices/$arInvoiceId"
                params={{ arInvoiceId: app.invoiceId }}
                className="font-medium text-primary hover:underline"
              >
                {app.invoice?.invoiceNumber ?? '—'}
              </Link>
              <span className="text-right font-medium tabular-nums">
                {formatAmount(app.appliedAmount)}
              </span>
              <span className="text-muted-foreground">
                {formatDate(app.applicationDate)}
              </span>
              <span className="w-7">
                {editable && app.allocationSource === 'user' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    disabled={removingId === app.id}
                    onClick={() => void handleRemove(app.id)}
                    aria-label={gt('Remove application', {
                      context:
                        'a11y label, remove a cash receipt application to an invoice',
                    })}
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                )}
              </span>
            </div>
          ))}
          {hiddenCount > 0 && (
            <p className="border-t px-3 py-1.5 text-xs text-muted-foreground">
              <T context="AR cash receipt, more invoice applications exist than are listed">
                Showing the first <Num>{paymentApplications.length}</Num> of{' '}
                <Num>{totalCount}</Num> applications. The total covers all of
                them.
              </T>
            </p>
          )}
          <div className="flex justify-end gap-1 border-t px-3 py-1.5 text-sm font-semibold text-muted-foreground">
            <T context="label for total amount of the receipt applied to invoices">
              Total applied:
            </T>
            <span className="text-foreground">
              {formatAmount(totalApplied.toFixed(2))}
            </span>
          </div>
        </div>
      )}

      {editable && loaded && (
        <ApplyToInvoiceDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          receiptId={receipt.id}
          customerId={receipt.customerId}
          defaultAmount={unapplied > 0 ? unapplied.toFixed(2) : null}
        />
      )}
    </div>
  );
}
